// frontend/src/pages/Trade.tsx - Order Book with Hold Invoice Atomic Swap
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '@/store/useAuthStore';
import { usePriceStore } from '@/store/usePriceStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import api from '@/lib/api';
import QRCode from 'qrcode.react';
import { 
  AlertCircle, 
  TrendingUp, 
  TrendingDown, 
  Info, 
  Plus, 
  X,
  ArrowLeftRight,
  Filter,
  Zap,
  User,
  Star,
  CheckCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

// Interfaces
interface Asset {
  id: string;
  name: string;
  symbol: string;
  enabled: boolean;
}

interface Order {
  id: number;
  user_id: number;
  order_type: 'buy' | 'sell';
  asset_id: string;
  amount_btc: number;
  price_usd?: number;
  dsa_enabled?: boolean;
  dsa_spread?: number;
  status: string;
  seller_lightning_address?: string;
  pending_liquidity?: boolean;
  created_at: string;
  user: {
    username: string;
    rating: number;
    total_trades: number;
  };
  asset: {
    symbol: string;
    name: string;
  };
}

// Helper functions
const btcToSats = (btc: number): number => Math.round(btc * 100000000);
const satsToBtc = (sats: number): number => sats / 100000000;

export function Trade() {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuthStore();
  
  // Order Book state
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterAsset, setFilterAsset] = useState('all');
  const [filterType, setFilterType] = useState<'all' | 'buy' | 'sell'>('all');
  
  // Pagination state
  const [sellOrdersPage, setSellOrdersPage] = useState(1);
  const [buyOrdersPage, setBuyOrdersPage] = useState(1);
  const ordersPerPage = 10;
  
  // Create Order Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [orderType, setOrderType] = useState<'buy' | 'sell'>('sell');
  const [amount, setAmount] = useState('');
  const [amountInSats, setAmountInSats] = useState('');
  const [price, setPrice] = useState('');
  const [dsaEnabled, setDsaEnabled] = useState(true);
  const [spreadPercent, setSpreadPercent] = useState('2.0');
  const [selectedAsset, setSelectedAsset] = useState('usdt-ta');
  const [creatingOrder, setCreatingOrder] = useState(false);
  const submittingRef = useRef(false);
  const [lightningAddress, setLightningAddress] = useState('');
  
  // Trade Confirmation Modal state
  const [tradeModalOpen, setTradeModalOpen] = useState(false);
  const [lnAddressPromptOpen, setLnAddressPromptOpen] = useState(false);
  const [lnAddressForTrade, setLnAddressForTrade] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [confirmingTrade, setConfirmingTrade] = useState(false);
  
  // Submarine Swap state
  const [swapInProgress, setSwapInProgress] = useState(false);
  const [pendingSwap, setPendingSwap] = useState<any>(null);
  const [currentSwap, setCurrentSwap] = useState<any>(null);
  const [swapStatusInterval, setSwapStatusInterval] = useState<NodeJS.Timeout | null>(null);
  const [makerInvoiceInput, setMakerInvoiceInput] = useState('');
  const [submittingMakerInvoice, setSubmittingMakerInvoice] = useState(false);
  // Converter widget state
  const [converterBtc, setConverterBtc] = useState('');
  const [converterSats, setConverterSats] = useState('');
  
  // Assets and prices - GET PRICE FROM ZUSTAND STORE
  const [assets, setAssets] = useState<Asset[]>([]);
  const { getPrice } = usePriceStore();
  const btcPrice = getPrice('btc')?.usd_price || 0;
  const [assetPrices, setAssetPrices] = useState<any>({});

  // Show own orders toggle (for testing)
  const [showOwnOrders, setShowOwnOrders] = useState(true);

  useEffect(() => {
    fetchAssets();
    fetchOrders();
    const interval = setInterval(() => {
      fetchOrders();
    }, 30000);
    return () => clearInterval(interval);
  }, [filterAsset, filterType]);

  // Fetch user profile to get saved Lightning Address
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get("/auth/me");
        if (response.data?.user?.lightning_address) {
          setLightningAddress(response.data.user.lightning_address);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    if (isAuthenticated) fetchProfile();
  }, [isAuthenticated]);

  // Cleanup swap polling on unmount
  useEffect(() => {
    return () => {
      if (swapStatusInterval) {
        clearInterval(swapStatusInterval);
      }
    };
  }, [swapStatusInterval]);

  // Poll for pending swaps (where current user needs to pay)
  useEffect(() => {
    if (!isAuthenticated || swapInProgress) return;
    const checkPending = async () => {
      try {
        const response = await api.get("/swaps/pending");
        if (response.data?.swaps?.length > 0) {
          setPendingSwap(response.data.swaps[0]);
        } else {
          setPendingSwap(null);
        }
      } catch (e) { console.error("Error checking pending swaps:", e); }
    };
    checkPending();
    const interval = setInterval(checkPending, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated, swapInProgress]);

  const fetchAssets = async () => {
    try {
      const response = await api.get('/assets');
      setAssets(response.data.assets || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params: any = {};
      
      if (filterAsset !== 'all') {
        params.asset_id = filterAsset;
      }
      if (filterType !== 'all') {
        params.order_type = filterType;
      }
      
      const response = await api.get('/orders', { params });
      
      // Filter orders
      const activeOrders = (response.data.orders || []).filter(
        (o: Order) => (o.status === 'active' || o.status === 'pending_liquidity') && (showOwnOrders || o.user_id !== user?.id)
      );
      
      // Sort by rating (highest first), then by total trades
      const sortedOrders = activeOrders.sort((a: Order, b: Order) => {
        if (b.user.rating !== a.user.rating) {
          return b.user.rating - a.user.rating;
        }
        return b.user.total_trades - a.user.total_trades;
      });
      
      setOrders(sortedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Amount conversion handlers
  const handleBtcAmountChange = (value: string) => {
    setAmount(value);
    if (value) {
      const sats = btcToSats(parseFloat(value));
      setAmountInSats(sats.toString());
    } else {
      setAmountInSats('');
    }
  };

  const handleSatsAmountChange = (value: string) => {
    setAmountInSats(value);
    if (value) {
      const btc = satsToBtc(parseInt(value));
      setAmount(btc.toFixed(8));
    } else {
      setAmount('');
    }
  };

  // Converter widget handlers
  const handleConverterBtcChange = (value: string) => {
    setConverterBtc(value);
    if (value) {
      const sats = btcToSats(parseFloat(value));
      setConverterSats(sats.toString());
    } else {
      setConverterSats('');
    }
  };

  const handleConverterSatsChange = (value: string) => {
    setConverterSats(value);
    if (value) {
      const btc = satsToBtc(parseInt(value));
      setConverterBtc(btc.toFixed(8));
    } else {
      setConverterBtc('');
    }
  };

  const setConverterQuickAmount = (sats: number) => {
    setConverterSats(sats.toString());
    const btc = satsToBtc(sats);
    setConverterBtc(btc.toFixed(8));
  };

  const calculateTotal = () => {
    const btcAmount = parseFloat(amount) || 0;
    const pricePerBtc = dsaEnabled ? btcPrice : (parseFloat(price) || 0);
    return (btcAmount * pricePerBtc).toFixed(2);
  };

  const calculateFee = (type: 'buy' | 'sell') => {
    const btcAmount = parseFloat(amount) || 0;
    const feePercent = type === 'buy' ? 1.0 : 0.0;
    return (btcAmount * (feePercent / 100)).toFixed(8);
  };

  const calculateTradeFee = (order: Order, isBuying: boolean) => {
    const btcAmount = Number(order.amount_btc);
    const feePercent = isBuying ? 1.0 : 0.0;
    return (btcAmount * (feePercent / 100)).toFixed(8);
  };

  // Calculate real-time price for DSA orders
  const getOrderPrice = (order: Order): number => {
    if (order.dsa_enabled && btcPrice > 0) {
      const spread = order.dsa_spread || 2.0;
      if (order.order_type === 'sell') {
        return btcPrice * (1 + spread / 100);
      } else {
        return btcPrice * (1 - spread / 100);
      }
    }
    return Number(order.price_usd) || 0;
  };

  const calculateTradeTotal = (order: Order, isBuying: boolean) => {
    const orderPrice = getOrderPrice(order);
    const baseAmount = Number(order.amount_btc) * orderPrice;
    if (isBuying) {
      const fee = baseAmount * 0.01;
      return (baseAmount + fee).toFixed(2);
    }
    return baseAmount.toFixed(2);
  };

  const handleCreateOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;  // block rapid double-submit
    submittingRef.current = true;
    
    if (!isAuthenticated) {
      alert('Please log in to create orders');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (!dsaEnabled && (!price || parseFloat(price) <= 0)) {
      alert('Please enter a valid price');
      return;
    }

    // Validate Lightning Address for sell orders
    const isTA = selectedAsset.includes('usdt') || selectedAsset.includes('usdc');
    if (orderType === 'sell' && !isTA && !lightningAddress.includes('@') && !lightningAddress.startsWith('lnbc')) {
      alert('Please enter a valid Lightning Address (e.g. you@getalby.com)');
      return;
    }

    try {
      setCreatingOrder(true);
      
      const btcAmount = parseFloat(amount);
      const pricePerBtc = dsaEnabled ? btcPrice : parseFloat(price);

      let description = `${orderType === 'buy' ? 'Buying' : 'Selling'} ${btcAmount} BTC`;
      if (dsaEnabled) {
        description += ` with DSA (${spreadPercent}% spread)`;
      }

      const orderData: any = {
        asset_id: selectedAsset,
        order_type: orderType,
        amount_btc: btcAmount,
        price_type: 'fixed',  
        price_usd: pricePerBtc,
        dsa_enabled: dsaEnabled,
        payment_method: 'Lightning Network',
        description,
      };
      
      if (dsaEnabled) {
        orderData.dsa_spread = parseFloat(spreadPercent);
      }

      // Add Lightning Address for sell orders
      if (orderType === 'sell' && lightningAddress) {
        orderData.seller_lightning_address = lightningAddress;
      }
  
      console.log('📤 Sending order data:', orderData);
      await api.post('/orders', orderData);
      
      alert('Order created successfully!');      // Save Lightning Address to profile if sell order      if (orderType === 'sell' && lightningAddress.includes('@')) {        try { await api.put('/auth/lightning-address', { lightning_address: lightningAddress }); } catch(e) { console.error(e); }      }
      setDrawerOpen(false);
      setAmount('');
      setAmountInSats('');
      setLightningAddress('');
      fetchOrders();
      
    } catch (error: any) {
      console.error('❌ Full error:', error);
      console.error('❌ Error response:', error.response?.data);
      
      let errorMessage = 'Failed to create order';
      
      if (error.response?.data) {
        const data = error.response.data;
        errorMessage = data.message || data.error || errorMessage;
        
        if (data.errors && Array.isArray(data.errors)) {
          errorMessage += ':\n' + data.errors.map((e: any) => `• ${e.msg}`).join('\n');
        }
      }
      
      alert(`Error: ${errorMessage}`);
    } finally {
      setCreatingOrder(false);
      submittingRef.current = false;
    }
  };

  const handleTakeOrder = (order: Order) => {
    // All swaps are BTC <-> stablecoin
    // BTC seller receives USDT-L/USDC-L via Speed Wallet invoice (submitted after match)
    // No Lightning Address needed - go straight to confirm
    setSelectedOrder(order);
    setTradeModalOpen(true);
  };

  // Handle confirm trade - initiate submarine swap
  const handleConfirmTrade = async () => {
    if (!selectedOrder) return;

    try {
      setConfirmingTrade(true);
      
      const response = await api.post('/swaps/initiate', {
        orderId: selectedOrder.id,
        seller_lightning_address: lnAddressForTrade || undefined,
        taker_lightning_address: lnAddressForTrade || undefined,
      });

      if (response.data.success) {
        const swap = response.data.swap;
        
        setTradeModalOpen(false);
        setCurrentSwap(swap);
        setSwapInProgress(true);
        
        if (!swap.waitingForMaker) {
          // BTC swap — regular flow with hold invoice
          startSwapStatusPolling(swap.swapId);
        } else {
          // TA swap — waiting for Maker to submit Speed invoice
          startSwapStatusPolling(swap.swapId);
        }
        
      } else {
        alert('Failed to initiate swap: ' + response.data.message);
      }
      
    } catch (error: any) {
      console.error('Error starting trade:', error);
      alert('Failed to start swap: ' + (error.response?.data?.message || 'Unknown error'));
    } finally {
      setConfirmingTrade(false);
    }
  };
  // Handle Maker submitting their Speed Wallet USDC invoice
  const handleSubmitMakerInvoice = async () => {
    if (!currentSwap || !makerInvoiceInput) return;

    if (!makerInvoiceInput.startsWith('lnbc')) {
      alert('Please paste a valid Speed Wallet invoice (starts with lnbc...)');
      return;
    }

    try {
      setSubmittingMakerInvoice(true);
      
      const response = await api.post(`/swaps/${currentSwap.swapId || currentSwap.id}/maker-invoice`, {
        speed_invoice: makerInvoiceInput,
      });

      if (response.data.success) {
        setCurrentSwap((prev: any) => ({
          ...prev,
          status: 'awaiting_payment',
          holdInvoice: response.data.makerHoldInvoice,
          amountSats: response.data.makerAmountSats,
        }));
        setMakerInvoiceInput('');
        // Start polling now that invoices are created
        startSwapStatusPolling(currentSwap.swapId || currentSwap.id);
      } else {
        alert('Failed to submit invoice: ' + response.data.message);
      }
    } catch (error: any) {
      console.error('Error submitting maker invoice:', error);
      alert('Failed: ' + (error.response?.data?.message || 'Unknown error'));
    } finally {
      setSubmittingMakerInvoice(false);
    }
  };
  // Start polling for swap status
  const startSwapStatusPolling = (swapId: number) => {
    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/swaps/${swapId}`);
        
        if (response.data.success) {
          const swapStatus = response.data.swap;
          if (swapStatus) {
            console.log('🔍 Poll response:', JSON.stringify(swapStatus));
            setCurrentSwap((prev: any) => {
              if (!prev) return swapStatus;
              return {
                ...prev,
                ...swapStatus,
                // Preserve critical fields from original pendingSwap
                isMaker: swapStatus.isMaker ?? prev.isMaker,
                isBuyer: swapStatus.isBuyer ?? prev.isBuyer,
                swapId: prev.swapId || swapStatus.id || swapStatus.swapId,
                holdInvoice: swapStatus.holdInvoice || prev.holdInvoice || '',
              };
            });
          }
          
          if (['completed', 'expired', 'cancelled', 'failed'].includes(swapStatus.status)) {
            stopSwapStatusPolling();
            
           if (swapStatus.status === 'completed') {
              fetchOrders();
             setTimeout(() => { setSwapInProgress(false); setCurrentSwap(null); }, 4000);
} 

            if (['expired', 'cancelled', 'failed'].includes(swapStatus.status)) {
              setTimeout(() => {
                setSwapInProgress(false);
                setCurrentSwap(null);
                fetchOrders();
              }, 3000);
            }
          }
        }
      } catch (error) {
        console.error('Error polling swap status:', error);
      }
    }, 5000);
    
    setSwapStatusInterval(interval);
  };

  const stopSwapStatusPolling = () => {
    if (swapStatusInterval) {
      clearInterval(swapStatusInterval);
      setSwapStatusInterval(null);
    }
  };

  const handleCancelSwap = async () => {
    if (!currentSwap) return;
    
    if (!confirm('Are you sure you want to cancel this swap?')) {
      return;
    }
    
    try {
      const response = await api.post(`/swaps/${currentSwap.swapId}/cancel`);
      
      if (response.data.success) {
        alert('Swap cancelled');
        setSwapInProgress(false);
        setCurrentSwap(null);
        stopSwapStatusPolling();
        fetchOrders();
      }
    } catch (error: any) {
      alert('Failed to cancel swap: ' + (error.response?.data?.message || 'Unknown error'));
    }
  };

  const handleCloseSwapModal = () => {
    setSwapInProgress(false);
    setCurrentSwap(null);
    stopSwapStatusPolling();
    fetchOrders();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Invoice copied to clipboard!');
  };

  // Filter and paginate orders
  const sellOrders = orders.filter(o => o.order_type === 'sell');
  const buyOrders = orders.filter(o => o.order_type === 'buy');

  const sellOrdersStart = (sellOrdersPage - 1) * ordersPerPage;
  const sellOrdersEnd = sellOrdersStart + ordersPerPage;
  const paginatedSellOrders = sellOrders.slice(sellOrdersStart, sellOrdersEnd);
  const totalSellPages = Math.ceil(sellOrders.length / ordersPerPage);

  const buyOrdersStart = (buyOrdersPage - 1) * ordersPerPage;
  const buyOrdersEnd = buyOrdersStart + ordersPerPage;
  const paginatedBuyOrders = buyOrders.slice(buyOrdersStart, buyOrdersEnd);
  const totalBuyPages = Math.ceil(buyOrders.length / ordersPerPage);

  if (!isAuthenticated) {
    return (
      <div className="container-main py-12">
        <div className="max-w-2xl mx-auto text-center">
          <AlertCircle className="w-16 h-16 text-apple-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">{t('ta_swap.auth_required', 'Authentication Required')}</h2>
          <p className="text-apple-gray-600 mb-6">
            Please log in to view the order book and start trading.
          </p>
          <Button variant="primary" onClick={() => window.location.href = '/login'}>
            Log In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-main py-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}

        {/* Pending Swap Banner */}
        {pendingSwap && !swapInProgress && (
         <div className="mb-4 bg-apple-yellow-50 border-2 border-apple-yellow-400 rounded-xl p-4 cursor-pointer hover:bg-apple-yellow-100 transition-colors" onClick={() => { setCurrentSwap(pendingSwap); setSwapInProgress(true); if (pendingSwap.swapId) { setTimeout(() => startSwapStatusPolling(pendingSwap.swapId), 3000); } }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="w-6 h-6 text-apple-yellow-600" />
                <div>
                  <p className="font-semibold text-apple-yellow-800">
                    {pendingSwap.isBtcSeller ? t('ta_swap.banner.action_needed') : pendingSwap.isBuyer ? t('ta_swap.banner.pending_trade') : t('ta_swap.banner.someone_wants_trade')}
                  </p>
                  <p className="text-sm text-apple-yellow-700">
                    {pendingSwap.amountSats} sats • {pendingSwap.isBuyer ? `${t('ta_swap.banner.seller', 'Seller')}: ${pendingSwap.sellerUsername}` : t('ta_swap.banner.waiting_buyer', 'Waiting for buyer to pay')} • {t('ta_swap.banner.expires', 'Expires')}: {new Date(pendingSwap.expiresAt).toLocaleTimeString()}
                  </p>
                </div>
              </div>
              <Button variant="primary" size="sm">{pendingSwap.isBuyer ? t('ta_swap.banner.pay_now', 'Pay Now') : t('ta_swap.banner.view', 'View')}</Button>
            </div>
          </div>
        )}

        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2">{t('trade.title')}</h1>
            <p className="text-apple-gray-600">
              {t('trade.subtitle')}
            </p>
          </div>
          <Button 
            variant="primary" 
            size="lg"
            onClick={() => setDrawerOpen(true)}
            className="flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('trade.createOrder')}
          </Button>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Main Order Book - Takes 4 columns (80%) */}
          <div className="lg:col-span-4 space-y-6">

            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-apple-gray-600" />
                    <span className="font-medium">{t('trade.filters')}</span>
                  </div>

                  <select
                    value={filterAsset}
                    onChange={(e) => setFilterAsset(e.target.value)}
                    className="px-4 py-2 rounded-lg border border-apple-gray-300 focus:ring-2 focus:ring-apple-blue-500"
                  >
                    <option value="all">{t('trade.allAssets')}</option>
                    {assets.filter(a => a.enabled).map(asset => (
                      <option key={asset.id} value={asset.id}>{asset.symbol}</option>
                    ))}
                  </select>

                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value as any)}
                    className="px-4 py-2 rounded-lg border border-apple-gray-300 focus:ring-2 focus:ring-apple-blue-500"
                  >
                    <option value="all">{t('trade.allOrders')}</option>
                    <option value="sell">{t('trade.sellOrders')}</option>
                    <option value="buy">{t('trade.buyOrders')}</option>
                  </select>

                  <label className="flex items-center gap-2 text-sm text-apple-gray-600 ml-auto">
                    <input
                      type="checkbox"
                      checked={showOwnOrders}
                      onChange={(e) => setShowOwnOrders(e.target.checked)}
                      className="rounded"
                    />
                    {t('trade.showMyOrders')}
                  </label> 
                </div>
              </CardContent>
            </Card>

            {/* Sell Orders */}
            {(filterType === 'all' || filterType === 'sell') && (
              <div>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-apple-green-500"></div>
                  {t('trade.sellersOffering')}
                  <span className="text-sm font-normal text-apple-gray-600">
                    ({t('trade.sellersSubtitle')})
                  </span>
                </h2> 
                
                {loading ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <div className="animate-spin w-8 h-8 border-4 border-apple-blue-500 border-t-transparent rounded-full mx-auto"></div>
                    </CardContent>
                  </Card>
                ) : sellOrders.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-apple-gray-600">
                      {t('ta_swap.no_sell_orders', 'No sell orders available')}
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <div className="space-y-2">
                      {paginatedSellOrders.map(order => (
                        <Card key={`${order.id}-${btcPrice}`} className={`hover:shadow-md transition-shadow relative ${order.status === 'pending_liquidity' ? 'opacity-60' : ''}`}>
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between gap-3 text-sm">
                              
                              {/* Left: Order Type Badge */}
                              <div className="flex items-center gap-2 min-w-[60px] flex-shrink-0">
                                <span className="px-2 py-0.5 bg-apple-green-100 text-apple-green-700 rounded text-xs font-semibold">
                                  SELL
                                </span>
                                {order.status === 'pending_liquidity' && (
                                  <span className="px-2 py-0.5 bg-apple-yellow-100 text-apple-yellow-700 rounded text-xs font-semibold">
                                    ⏳ LOW LIQ
                                  </span>
                                )}
                              </div>

                              {/* Middle: Trade Details */}
                              <div className="flex items-center gap-2 flex-1 text-xs">
                                <span className="font-semibold">{Number(order.amount_btc).toFixed(8)} BTC</span>
                                <span className="text-apple-gray-600">@</span>
                                <span className="font-semibold">${getOrderPrice(order).toFixed(2)}</span>
                                <span className="text-apple-gray-500">{t('trade.for')}</span>
                                <span className="font-semibold text-apple-blue-600">
                                  {order.asset.symbol === 'USDT' ? 'USDT-TA' : 
                                   order.asset.symbol === 'USDC' ? 'USDC-TA' : 
                                   order.asset.symbol}
                                </span>
                                {order.dsa_enabled && (
                                  <span className="text-xs px-1.5 py-0.5 bg-apple-orange-100 text-apple-orange-700 rounded ml-1">
                                    DSA {order.dsa_spread}%
                                  </span>
                                )}
                                <span className="text-apple-gray-400">
                                  ({btcToSats(Number(order.amount_btc)).toLocaleString()} sats)
                                  {(order.asset.symbol === 'USDT' || order.asset.symbol === 'USDC') && (
                                    <span className="ml-1 text-apple-green-600 font-semibold">
                                      ≈ ${(Number(order.amount_btc) * getOrderPrice(order)).toFixed(2)}
                                    </span>
                                  )}
                                </span>
                              </div>

                              {/* User Rating */}
                              <div className="flex items-center gap-1 text-xs text-apple-gray-600 min-w-[120px] flex-shrink-0">
                                <User className="w-3 h-3" />
                                <span>{order.user.username}</span>
                                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 ml-1" />
                                <span className="font-semibold">{order.user.rating.toFixed(1)}</span>
                                <span className="text-apple-gray-400">({order.user.total_trades})</span>
                              </div>

                              {/* Right: Fee & Buttons */}
                              <div className="flex flex-col items-end gap-1 ml-auto flex-shrink-0">
                                <span className="text-xs text-apple-gray-600 whitespace-nowrap">{t('trade.fee')}: 1%</span>
                                
                                {order.user_id === user?.id ? (
                                  <Button 
                                    variant="secondary" 
                                    size="sm"
                                    onClick={async () => {
                                      if (confirm('Cancel this order?')) {
                                        try {
                                          await api.delete(`/orders/${order.id}`);
                                          fetchOrders();
                                          alert('Order cancelled');
                                        } catch (error) {
                                          console.error('Error:', error);
                                          alert('Failed to cancel');
                                        }
                                      }
                                    }}
                                    className="whitespace-nowrap bg-apple-red-100 text-apple-red-700 hover:bg-apple-red-200"
                                  >
                                    {t('trade.cancel')} 
                                  </Button>
                                ) : (
                                  <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={() => handleTakeOrder(order)}
                                    className="whitespace-nowrap"
                                    disabled={order.status === 'pending_liquidity'}
                                  >
                                    {order.status === 'pending_liquidity' ? '⏳ Pending' : t('trade.buyBTC')}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalSellPages > 1 && (
                      <div className="mt-4 flex items-center justify-between">
                        <div className="text-sm text-apple-gray-600">
                          Showing {sellOrdersStart + 1}-{Math.min(sellOrdersEnd, sellOrders.length)} of {sellOrders.length} orders
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="secondary" size="sm" onClick={() => setSellOrdersPage(p => Math.max(1, p - 1))} disabled={sellOrdersPage === 1} className="flex items-center gap-1">
                            <ChevronLeft className="w-4 h-4" /> Prev
                          </Button>
                          <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(totalSellPages, 5) }, (_, i) => {
                              let page;
                              if (totalSellPages <= 5) { page = i + 1; }
                              else if (sellOrdersPage <= 3) { page = i + 1; }
                              else if (sellOrdersPage >= totalSellPages - 2) { page = totalSellPages - 4 + i; }
                              else { page = sellOrdersPage - 2 + i; }
                              return (
                                <button key={page} onClick={() => setSellOrdersPage(page)} className={`px-3 py-1 rounded text-sm ${page === sellOrdersPage ? 'bg-apple-blue-500 text-white font-semibold' : 'bg-apple-gray-100 text-apple-gray-700 hover:bg-apple-gray-200'}`}>
                                  {page}
                                </button>
                              );
                            })}
                          </div>
                          <Button variant="secondary" size="sm" onClick={() => setSellOrdersPage(p => Math.min(totalSellPages, p + 1))} disabled={sellOrdersPage === totalSellPages} className="flex items-center gap-1">
                            Next <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Buy Orders */}
            {(filterType === 'all' || filterType === 'buy') && (
              <div>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-apple-blue-500"></div>
                  {t('trade.buyersWanting')}
                  <span className="text-sm font-normal text-apple-gray-600">
                    ({t('trade.buyersSubtitle')})
                  </span>
                </h2>

                {loading ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <div className="animate-spin w-8 h-8 border-4 border-apple-blue-500 border-t-transparent rounded-full mx-auto"></div>
                    </CardContent>
                  </Card>
                ) : buyOrders.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-apple-gray-600">
                      {t('ta_swap.no_buy_orders', 'No buy orders available')}
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <div className="space-y-2">
                      {paginatedBuyOrders.map(order => (
                        <Card key={`${order.id}-${btcPrice}`} className={`hover:shadow-md transition-shadow relative ${order.status === 'pending_liquidity' ? 'opacity-60' : ''}`}>
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between gap-3 text-sm">
                              
                              <div className="flex items-center gap-2 min-w-[60px] flex-shrink-0">
                                <span className="px-2 py-0.5 bg-apple-blue-100 text-apple-blue-700 rounded text-xs font-semibold">
                                  BUY
                                </span>
                                {order.status === 'pending_liquidity' && (
                                  <span className="px-2 py-0.5 bg-apple-yellow-100 text-apple-yellow-700 rounded text-xs font-semibold">
                                    ⏳ LOW LIQ
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center gap-2 flex-1 text-xs">
                                <span className="font-semibold">{Number(order.amount_btc).toFixed(8)} BTC</span>
                                <span className="text-apple-gray-600">@</span>
                                <span className="font-semibold">${getOrderPrice(order).toFixed(2)}</span>
                                <span className="text-apple-gray-500">{t('trade.for')}</span>
                                <span className="font-semibold text-apple-blue-600">
                                  {order.asset.symbol === 'USDT' ? 'USDT-TA' : 
                                   order.asset.symbol === 'USDC' ? 'USDC-TA' : 
                                   order.asset.symbol}
                                </span>
                                <span className="text-apple-gray-400">
                                  ({btcToSats(Number(order.amount_btc)).toLocaleString()} sats)
                                  {(order.asset.symbol === 'USDT' || order.asset.symbol === 'USDC') && (
                                    <span className="ml-1 text-apple-green-600 font-semibold">
                                      ≈ ${(Number(order.amount_btc) * getOrderPrice(order)).toFixed(2)}
                                    </span>
                                  )}
                                </span>
                              </div>

                              <div className="flex items-center gap-1 text-xs text-apple-gray-600 min-w-[120px] flex-shrink-0">
                                <User className="w-3 h-3" />
                                <span>{order.user.username}</span>
                                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 ml-1" />
                                <span className="font-semibold">{order.user.rating.toFixed(1)}</span>
                                <span className="text-apple-gray-400">({order.user.total_trades})</span>
                              </div>

                              <div className="flex flex-col items-end gap-1 ml-auto flex-shrink-0">
                                <span className="text-xs text-apple-gray-600 whitespace-nowrap">{t('trade.fee')}: 0%</span>
                                
                                {order.user_id === user?.id ? (
                                  <Button 
                                    variant="secondary" 
                                    size="sm"
                                    onClick={async () => {
                                      if (confirm('Cancel this order?')) {
                                        try {
                                          await api.delete(`/orders/${order.id}`);
                                          fetchOrders();
                                          alert('Order cancelled');
                                        } catch (error) {
                                          console.error('Error:', error);
                                          alert('Failed to cancel');
                                        }
                                      }
                                    }}
                                    className="whitespace-nowrap bg-apple-red-100 text-apple-red-700 hover:bg-apple-red-200"
                                  >
                                    {t('trade.cancel')}
                                  </Button>
                                ) : (
                                  <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={() => handleTakeOrder(order)}
                                    className="whitespace-nowrap"
                                    disabled={order.status === 'pending_liquidity'}
                                  >
                                    {order.status === 'pending_liquidity' ? '⏳ Pending' : t('trade.sellBTC')}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalBuyPages > 1 && (
                      <div className="mt-4 flex items-center justify-between">
                        <div className="text-sm text-apple-gray-600">
                          Showing {buyOrdersStart + 1}-{Math.min(buyOrdersEnd, buyOrders.length)} of {buyOrders.length} orders
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="secondary" size="sm" onClick={() => setBuyOrdersPage(p => Math.max(1, p - 1))} disabled={buyOrdersPage === 1} className="flex items-center gap-1">
                            <ChevronLeft className="w-4 h-4" /> Prev
                          </Button>
                          <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(totalBuyPages, 5) }, (_, i) => {
                              let page;
                              if (totalBuyPages <= 5) { page = i + 1; }
                              else if (buyOrdersPage <= 3) { page = i + 1; }
                              else if (buyOrdersPage >= totalBuyPages - 2) { page = totalBuyPages - 4 + i; }
                              else { page = buyOrdersPage - 2 + i; }
                              return (
                                <button key={page} onClick={() => setBuyOrdersPage(page)} className={`px-3 py-1 rounded text-sm ${page === buyOrdersPage ? 'bg-apple-blue-500 text-white font-semibold' : 'bg-apple-gray-100 text-apple-gray-700 hover:bg-apple-gray-200'}`}>
                                  {page}
                                </button>
                              );
                            })}
                          </div>
                          <Button variant="secondary" size="sm" onClick={() => setBuyOrdersPage(p => Math.min(totalBuyPages, p + 1))} disabled={buyOrdersPage === totalBuyPages} className="flex items-center gap-1">
                            Next <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-6 space-y-3">
              {/* Market Price */}
              <Card>
                <CardHeader className="pb-2 pt-3 px-3">
                  <CardTitle className="text-sm">{t('trade.marketPrice')}</CardTitle>
                </CardHeader>
                <CardContent className="pb-3 px-3">
                  <div className="text-xl font-bold text-apple-blue-600 mb-0.5">
                    ${btcPrice.toFixed(2)}
                  </div>
                  <p className="text-xs text-apple-gray-600">{t('trade.bitcoin')}</p>
                  <p className="text-xs text-apple-gray-500 mt-0.5">
                    {t('trade.updatesEvery')}
                  </p>
                </CardContent>
              </Card>

              {/* SATS Converter Widget */}
              <Card className="bg-apple-blue-50 border-apple-blue-200">
                <CardHeader className="pb-1.5 pt-2.5 px-3">
                  <CardTitle className="flex items-center gap-1.5 text-apple-blue-700 text-sm">
                    <ArrowLeftRight className="w-3.5 h-3.5" />
                    {t('trade.satsConverter')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5 pb-2.5 px-3">
                  <div>
                    <label className="text-xs font-medium text-apple-gray-700">BTC</label>
                    <input
                      type="number"
                      step="0.00000001"
                      value={converterBtc}
                      onChange={(e) => handleConverterBtcChange(e.target.value)}
                      placeholder="0.00000000"
                      className="w-full px-2 py-1 text-xs rounded border border-apple-gray-300 focus:ring-1 focus:ring-apple-blue-500"
                    />
                  </div>
                  
                  <div className="text-center py-0.5">
                    <ArrowLeftRight className="w-2.5 h-2.5 mx-auto text-apple-gray-400" />
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-apple-gray-700">SATS</label>
                    <input
                      type="number"
                      step="1"
                      value={converterSats}
                      onChange={(e) => handleConverterSatsChange(e.target.value)}
                      placeholder="0"
                      className="w-full px-2 py-1 text-xs rounded border border-apple-gray-300 focus:ring-1 focus:ring-apple-blue-500"
                    />
                  </div>

                  <div className="pt-1.5 border-t border-apple-blue-200">
                    <p className="text-xs text-apple-gray-600 mb-1">{t('trade.quick')}</p>
                    <div className="grid grid-cols-3 gap-1">
                      <button onClick={() => setConverterQuickAmount(100000)} className="px-1.5 py-0.5 text-xs bg-white rounded border border-apple-gray-300 hover:bg-apple-gray-50">100K</button>
                      <button onClick={() => setConverterQuickAmount(500000)} className="px-1.5 py-0.5 text-xs bg-white rounded border border-apple-gray-300 hover:bg-apple-gray-50">500K</button>
                      <button onClick={() => setConverterQuickAmount(1000000)} className="px-1.5 py-0.5 text-xs bg-white rounded border border-apple-gray-300 hover:bg-apple-gray-50">1M</button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Trading Info */}
              <Card>
                <CardHeader className="pb-1.5 pt-2.5 px-3">
                  <CardTitle className="text-sm">{t('trade.feeStructure')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-xs pb-2.5 px-3">
                  <div className="flex justify-between">
                    <span className="text-apple-gray-600">{t('trade.buyers')}</span>
                    <span className="font-semibold">1%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-apple-gray-600">{t('trade.sellers')}</span>
                    <span className="font-semibold text-apple-green-600">0% 🎉</span>
                  </div>
                  <div className="pt-1 border-t border-apple-gray-200 text-xs text-apple-gray-600">
                    <Info className="w-3 h-3 inline mr-0.5" />
                    {t('trade.feesSupport')}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Create Order Slide-in Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setDrawerOpen(false)}
          ></div>
          
          <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">{t('trade.drawer.title')}</h2>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="p-2 hover:bg-apple-gray-100 rounded-lg"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleCreateOrder} className="space-y-6">
                {/* Buy/Sell Tabs */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setOrderType('buy')}
                    className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${
                      orderType === 'buy'
                        ? 'bg-apple-green-500 text-white'
                        : 'bg-apple-gray-100 text-apple-gray-700'
                    }`}
                  >
                    <TrendingUp className="w-5 h-5 inline mr-2" />
                    {t('trade.drawer.buyTab')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setOrderType('sell')}
                    className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${
                      orderType === 'sell'
                        ? 'bg-apple-red-500 text-white'
                        : 'bg-apple-gray-100 text-apple-gray-700'
                    }`}
                  >
                    <TrendingDown className="w-5 h-5 inline mr-2" />
                    {t('trade.drawer.sellTab')}
                  </button>
                </div>

                {/* Asset Selection */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t('trade.drawer.asset')}
                  </label>
                  <select
                    value={selectedAsset}
                    onChange={(e) => setSelectedAsset(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-apple-gray-300 focus:ring-2 focus:ring-apple-blue-500"
                  >
                    {assets.filter(a => a.enabled && a.id !== 'btc').map(asset => (
                      <option key={asset.id} value={asset.id}>
                        {asset.symbol} - {asset.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Lightning Address - SELL ORDERS ONLY */}
                {orderType === 'sell' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {(selectedAsset.includes('usdt') || selectedAsset.includes('usdc'))
                        ? '⚡ Lightning Address (optional — you will receive USDC-L via Speed Wallet)'
                        : '⚡ Lightning Address (where you receive sats)'}
                    </label>
                    <input
                      type="text"
                      value={lightningAddress}
                      onChange={(e) => setLightningAddress(e.target.value)}
                      placeholder="you@getalby.com"
                      className="w-full px-4 py-3 rounded-lg border border-apple-gray-300 focus:ring-2 focus:ring-apple-blue-500"
                      required={!(selectedAsset.includes('usdt') || selectedAsset.includes('usdc'))}
                    />
                    <p className="text-xs text-apple-gray-600 mt-1">
                      When a buyer takes your order, payment is sent to this address via Lightning Network.
                    </p>
                  </div>
                )}

                {/* Amount */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {t('trade.drawer.amountBTC')}
                    </label>
                    <input
                      type="number"
                      step="0.00000001"
                      min="0.00000001"
                      value={amount}
                      onChange={(e) => handleBtcAmountChange(e.target.value)}
                      placeholder="0.00000000"
                      className="w-full px-4 py-3 rounded-lg border border-apple-gray-300 focus:ring-2 focus:ring-apple-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      required
                    />
                  </div>
                  
                  <div className="text-center text-apple-gray-500 text-sm">
                    = {amountInSats ? parseInt(amountInSats).toLocaleString() : '0'} sats
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {t('trade.drawer.orEnterSats')}
                    </label>
                    <input
                      type="number"
                      step="1"
                      value={amountInSats}
                      onChange={(e) => handleSatsAmountChange(e.target.value)}
                      placeholder="0"
                      className="w-full px-4 py-3 rounded-lg border border-apple-gray-300 focus:ring-2 focus:ring-apple-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                </div>

                {/* Price - Only show if DSA is disabled */}
                {!dsaEnabled && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {t('trade.drawer.pricePerBTC')}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-apple-gray-300 focus:ring-2 focus:ring-apple-blue-500"
                      required={!dsaEnabled}
                    />
                    <p className="text-sm text-apple-gray-600 mt-1">
                      {t('trade.drawer.current')}: ${btcPrice.toFixed(2)}
                    </p>
                  </div>
                )}

                {/* DSA Toggle for SELL Orders */}
                {orderType === 'sell' && (
                  <div className="bg-apple-green-50 border border-apple-green-200 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <input
                        type="checkbox"
                        id="dsa-enabled-drawer"
                        checked={dsaEnabled}
                        onChange={(e) => setDsaEnabled(e.target.checked)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <label htmlFor="dsa-enabled-drawer" className="font-medium cursor-pointer">
                          {t('trade.drawer.enableDSA')}
                        </label>
                        <p className="text-sm text-apple-gray-600">
                          {t('trade.drawer.dsaDescription')} <span className="text-apple-green-700 font-semibold">{t('trade.drawer.recommended')}</span>
                        </p>
                        <a 
                          href="/about#dsa" 
                          target="_blank"
                          className="text-xs text-apple-green-600 hover:text-apple-green-700 hover:underline font-medium inline-flex items-center gap-1 mt-2"
                        >
                          {t('trade.drawer.learnMore')}
                        </a>
                      </div>
                    </div>
                    
                    {dsaEnabled && (
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          {t('trade.drawer.spreadPercent')}
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0.5"
                          max="5.0"
                          value={spreadPercent}
                          onChange={(e) => setSpreadPercent(e.target.value)}
                          className="w-full px-4 py-2 rounded-lg border border-apple-gray-300 focus:ring-2 focus:ring-apple-green-500"
                        />
                        <p className="text-xs text-apple-gray-600 mt-1">
                          Your profit margin above market (0.5% - 5.0%)
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* DSA Toggle for BUY Orders */}
                {orderType === 'buy' && (
                  <div className="bg-apple-blue-50 border border-apple-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <input
                        type="checkbox"
                        id="dsa-enabled-drawer-buy"
                        checked={dsaEnabled}
                        onChange={(e) => setDsaEnabled(e.target.checked)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <label htmlFor="dsa-enabled-drawer-buy" className="font-medium cursor-pointer">
                          {t('trade.drawer.enableDSA')}
                        </label>
                        <p className="text-sm text-apple-gray-600">
                          Auto-adjust your bid with market • Stay competitive 24/7 • <span className="text-apple-blue-700 font-semibold">{t('trade.drawer.recommended')}</span>
                        </p>
                        <a 
                          href="/about#dsa" 
                          target="_blank"
                          className="text-xs text-apple-blue-600 hover:text-apple-blue-700 hover:underline font-medium inline-flex items-center gap-1 mt-2"
                        >
                          {t('trade.drawer.learnMore')}
                        </a>
                      </div>
                    </div>
                    
                    {dsaEnabled && (
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          {t('trade.drawer.spreadPercent')}
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0.5"
                          max="5.0"
                          value={spreadPercent}
                          onChange={(e) => setSpreadPercent(e.target.value)}
                          className="w-full px-4 py-2 rounded-lg border border-apple-gray-300 focus:ring-2 focus:ring-apple-blue-500"
                        />
                        <p className="text-xs text-apple-gray-600 mt-1">
                          Your discount below market (0.5% - 5.0%)
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Fee Breakdown */}
                <div className="bg-apple-gray-50 rounded-lg p-4 space-y-2">
                  <h3 className="font-semibold mb-2">{t('trade.drawer.feeBreakdown')}</h3>
                  <div className="flex justify-between text-sm">
                    <span>{t('trade.drawer.total')}</span>
                    <span className="font-semibold">${calculateTotal()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{t('trade.fee')} ({orderType === 'buy' ? '1%' : '0%'}):</span>
                    <span className="font-semibold">
                      {calculateFee(orderType)} BTC
                      {orderType === 'sell' && <span className="text-apple-green-600 ml-2">{t('trade.drawer.free')}</span>}
                    </span>
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full py-4 text-lg"
                  disabled={creatingOrder}
                >
                  {creatingOrder 
                    ? t('trade.drawer.creating') 
                    : orderType === 'buy' 
                      ? t('trade.drawer.createBuyOrder') 
                      : t('trade.drawer.createSellOrder')
                  }
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}


      {/* Lightning Address Prompt - when taking a buy order */}
      {lnAddressPromptOpen && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setLnAddressPromptOpen(false)}></div>
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">⚡ Your Lightning Address</h2>
              <button onClick={() => setLnAddressPromptOpen(false)} className="p-2 hover:bg-apple-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-apple-gray-600 mb-4">Enter the Lightning Address where you want to receive sats for this trade.</p>
            <input type="text" value={lnAddressForTrade} onChange={(e) => setLnAddressForTrade(e.target.value)} placeholder="you@getalby.com" className="w-full px-4 py-3 rounded-lg border border-apple-gray-300 focus:ring-2 focus:ring-apple-blue-500 mb-2" />
            <label className="flex items-center gap-2 text-xs text-apple-gray-600 mb-4"><input type="checkbox" defaultChecked onChange={async (e) => { if (e.target.checked && lnAddressForTrade.includes("@")) { try { await api.put("/auth/lightning-address", { lightning_address: lnAddressForTrade }); setLightningAddress(lnAddressForTrade); } catch(err) { console.error(err); } } }} /> Save as default</label>
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={() => setLnAddressPromptOpen(false)}>Cancel</Button>
              <Button variant="primary" className="flex-1" onClick={() => { if (!lnAddressForTrade.includes("@")) { alert("Please enter a valid Lightning Address"); return; } setLnAddressPromptOpen(false); setTradeModalOpen(true); }} disabled={!lnAddressForTrade.includes("@")}>Continue</Button>
            </div>
          </div>
        </div>
      )}

      {/* Trade Confirmation Modal */}
      {tradeModalOpen && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setTradeModalOpen(false)}
          ></div>
          
          <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">{t('trade.confirm_trade')}</h2>
              <button
                onClick={() => setTradeModalOpen(false)}
                className="p-2 hover:bg-apple-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-apple-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    selectedOrder.order_type === 'sell'
                      ? 'bg-apple-green-100 text-apple-green-700'
                      : 'bg-apple-blue-100 text-apple-blue-700'
                  }`}>
                    {selectedOrder.order_type === 'sell' ? t('ta_swap.confirm.buying', 'BUYING') : t('ta_swap.confirm.selling', 'SELLING')}
                  </span>
                  <span className="font-semibold">{selectedOrder.asset.symbol}</span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-apple-gray-600">{t('ta_swap.confirm.amount', 'Amount:')}</span>
                    <div className="font-semibold">
                      {Number(selectedOrder.amount_btc).toFixed(8)} BTC
                    </div>
                    <div className="text-xs text-apple-gray-500">
                      {btcToSats(Number(selectedOrder.amount_btc)).toLocaleString()} sats
                    </div>
                  </div>
                  <div>
                    <span className="text-apple-gray-600">{t('ta_swap.confirm.price', 'Price:')}</span>
                    <div className="font-semibold">
                      ${getOrderPrice(selectedOrder).toFixed(2)}
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-apple-blue-200">
                  <div className="flex items-center gap-2 text-sm text-apple-gray-600">
                    <User className="w-4 h-4" />
                    <span>{selectedOrder.user.username}</span>
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <span>{selectedOrder.user.rating.toFixed(1)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-apple-gray-50 rounded-lg p-4">
                <h3 className="font-semibold mb-3">{t('ta_swap.confirm.payment_summary', 'Payment Summary')}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t('ta_swap.confirm.base_amount', 'Base Amount:')}</span>
                    <span className="font-semibold">
                      ${(Number(selectedOrder.amount_btc) * getOrderPrice(selectedOrder)).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                      <span>{t('ta_swap.confirm.fee_label')}</span>
                    <span className="font-semibold">
                      ${(Number(selectedOrder.amount_btc) * getOrderPrice(selectedOrder) * 0.01).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-apple-gray-300 text-base">
                    <span className="font-semibold">{t('ta_swap.confirm.you_pay')}</span>
                    <span className="font-bold text-apple-blue-600">
                     ${calculateTradeTotal(selectedOrder, true)} 
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-apple-yellow-50 border border-apple-yellow-200 rounded-lg p-3 text-sm">
                <div className="flex gap-2">
                  <Info className="w-5 h-5 text-apple-yellow-700 flex-shrink-0" />
                  <div className="text-apple-yellow-800">
                    <p className="font-medium mb-1">{t('ta_swap.confirm.how_it_works')}</p>
                    <ul className="text-xs space-y-1">
                      <li>{t('ta_swap.confirm.step1')}</li>
                      <li>{t('ta_swap.confirm.step2')}</li>
                      <li>{t('ta_swap.confirm.step3')}</li>
                      <li>{t('ta_swap.confirm.step4')}</li>
                      <li>{t('ta_swap.confirm.step5')}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setTradeModalOpen(false)}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="primary"
                  className="flex-1 flex items-center justify-center gap-2"
                  onClick={handleConfirmTrade}
                  disabled={confirmingTrade}
                >
                  {confirmingTrade ? (
                    t('ta_swap.confirm.starting', 'Starting...')
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      {t('trade.confirm_trade')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submarine Swap In Progress Modal — Hold Invoice Flow */}
      {swapInProgress && currentSwap && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => {}}
          ></div>
          
          <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">{t('atomic_swap.title')} ⚡</h2>
              {(
                <button
                  onClick={handleCloseSwapModal}
                  className="p-2 hover:bg-apple-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

           {/* Status Indicator */}
            <div className="mb-6">
              {/* MATCHED — Waiting for Maker's Speed invoice */}
              {currentSwap.status === 'matched' && (currentSwap.isBtcSeller) && (
                <div style={{background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: '8px', padding: '16px'}}>
                  <p style={{fontWeight: 'bold', marginBottom: '8px'}}>{t('ta_swap.maker_form.title')}</p>
                  <div style={{fontSize: '14px', marginBottom: '12px'}}>
                    <p style={{marginBottom: '4px'}}>{t('ta_swap.maker_form.open_speed_wallet')}</p>
                    <p>{t('ta_swap.maker_form.step_receive', { asset: 'USDC/USDT' })}</p>
                    <p>{t('ta_swap.maker_form.step_amount', { amount: currentSwap.usdcAmount ? Number(currentSwap.usdcAmount).toFixed(2) : '...' })}</p>
                    <p>{t('ta_swap.maker_form.step_paste')}</p>
                  </div>
                  <input
                    type="text"
                    value={makerInvoiceInput || ''}
                    onChange={(e) => setMakerInvoiceInput(e.target.value)}
                    placeholder={t('ta_swap.maker_form.placeholder')}
                    style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', marginBottom: '8px', boxSizing: 'border-box'}}
                  />
                  <button
                    onClick={handleSubmitMakerInvoice}
                    disabled={submittingMakerInvoice || !makerInvoiceInput}
                    style={{width: '100%', padding: '10px', background: submittingMakerInvoice ? '#ccc' : '#ff9800', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold'}}
                  >
                    {submittingMakerInvoice ? t('ta_swap.maker_form.submitting') : t('ta_swap.maker_form.submit')}
                  </button>
                </div>
              )}

              {currentSwap.status === 'matched' && !(currentSwap.isBtcSeller) && (
                <div className="bg-apple-blue-50 border border-apple-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-apple-blue-500 border-t-transparent rounded-full"></div>
                    <span className="font-semibold text-apple-blue-700">{t('ta_swap.status.waiting_speed_invoice')}</span>
                  </div>
                  <p className="text-xs text-apple-blue-600 mt-2">{t('ta_swap.status.waiting_timeout', 'They have been notified and have 10 minutes to respond.')}</p>
                </div>
              )}

              {/* AWAITING PAYMENT — Both hold invoices ready */}
              {currentSwap.status === 'awaiting_payment' && (
                <div className="bg-apple-blue-50 border border-apple-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-apple-blue-500 border-t-transparent rounded-full"></div>
                    <span className="font-semibold text-apple-blue-700">{t('ta_swap.status.both_must_pay')}</span>
                  </div>
                </div>
              )}

              {/* ONE PARTY PAID */}
              {currentSwap.status === 'maker_paid' && (
                <div className="bg-apple-yellow-50 border border-apple-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-apple-yellow-500 border-t-transparent rounded-full"></div>
                    <span className="font-semibold text-apple-yellow-700">
                      {currentSwap.isMaker ? t('ta_swap.status.maker_paid_waiting') : t('ta_swap.status.seller_paid_pay_now')}
                    </span>
                  </div>
                </div>
              )}

              {currentSwap.status === 'taker_paid' && (
                <div className="bg-apple-yellow-50 border border-apple-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-apple-yellow-500 border-t-transparent rounded-full"></div>
                    <span className="font-semibold text-apple-yellow-700">
                      {currentSwap.isMaker ? t('ta_swap.status.buyer_paid_pay_now') : t('ta_swap.status.taker_paid_waiting')}
                    </span>
                  </div>
                </div>
              )}

              {/* BOTH ACCEPTED / SETTLING */}
              {(currentSwap.status === 'both_accepted' || currentSwap.status === 'settling') && (
                <div className="bg-apple-green-50 border border-apple-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-apple-green-500 border-t-transparent rounded-full"></div>
                    <span className="font-semibold text-apple-green-700">{t('ta_swap.status.settling', 'Both parties paid! Settling the swap...')}</span>
                  </div>
                </div>
              )}

              {/* LEGACY BTC STATES */}
              {currentSwap.status === 'pending' && (
                <div className="bg-apple-blue-50 border border-apple-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-apple-blue-500 border-t-transparent rounded-full"></div>
                    <span className="font-semibold text-apple-blue-700">{t('ta_swap.status.pay_to_start')}</span>
                  </div>
                </div>
              )}
              
              {currentSwap.status === 'paying_seller' && (
                <div className="bg-apple-yellow-50 border border-apple-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-apple-yellow-500 border-t-transparent rounded-full"></div>
                    <span className="font-semibold text-apple-yellow-700">{t('ta_swap.status.sending_to_seller', 'Sats held! Sending to seller...')}</span>
                  </div>
                </div>
              )}

              {/* COMPLETED */}
              {currentSwap.status === 'completed' && (
                <div className="bg-apple-green-50 border border-apple-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-apple-green-600" />
                    <span className="font-semibold text-apple-green-700">{t('ta_swap.status.completed')}</span>
                  </div>
                </div>
              )}
              
              {currentSwap.status === 'expired' && (
                <div className="bg-apple-red-50 border border-apple-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-apple-red-600" />
                    <span className="font-semibold text-apple-red-700">{t('ta_swap.status.expired', 'Swap expired — you have been refunded')}</span>
                  </div>
                </div>
              )}
              
              {currentSwap.status === 'cancelled' && (
                <div className="bg-apple-gray-50 border border-apple-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <X className="w-5 h-5 text-apple-gray-600" />
                    <span className="font-semibold text-apple-gray-700">{t('ta_swap.status.cancelled', 'Swap cancelled — you have been refunded')}</span>
                  </div>
                </div>
              )}

              {currentSwap.status === 'failed' && (
                <div className="bg-apple-red-50 border border-apple-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-apple-red-600" />
                    <span className="font-semibold text-apple-red-700">{t('ta_swap.status.failed', 'Swap failed — you have been refunded')}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Step — Single Hold Invoice */}
            {!['completed', 'expired', 'cancelled', 'failed', 'matched'].includes(currentSwap.status) && (
              <>
                {/* Step 1: Pay Hold Invoice */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      !['pending', 'awaiting_payment', 'maker_paid', 'taker_paid'].includes(currentSwap.status) ? 'bg-apple-green-500 text-white' : 'bg-apple-blue-500 text-white'
                    }`}>
                      {currentSwap.status !== 'pending' ? '✓' : '1'}
                    </div>
                    <h3 className="text-lg font-semibold">
                      {!['pending', 'awaiting_payment', 'maker_paid', 'taker_paid'].includes(currentSwap.status) ? t('ta_swap.hold_invoice.payment_received', 'Payment received ✅') : t('ta_swap.hold_invoice.pay_title', 'Pay Hold Invoice')}
                    </h3>
                  </div>

                  {(['pending', 'awaiting_payment', 'maker_paid', 'taker_paid'].includes(currentSwap.status)) && currentSwap.holdInvoice && (
                    <div className="bg-white border-2 border-apple-gray-300 rounded-lg p-4">
                      <div className="flex justify-center mb-4">
                        <QRCode 
                          value={currentSwap.holdInvoice} 
                          size={256}
                          level="H"
                          includeMargin={true}
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-apple-gray-700">
                          {t('ta_swap.hold_invoice.amount')} {currentSwap.amount}
                        </div>
                        <div className="text-xs text-apple-gray-500">
                         {t('ta_swap.hold_invoice.description')}
                        </div>
                        <div className="text-xs text-apple-blue-600 font-semibold mt-1">
                          {currentSwap.isMaker
                            ? '💡 ' + t('ta_swap.hold_invoice.pay_phoenix', 'Pay from Phoenix or any Lightning wallet')
                            : '💡 ' + t('ta_swap.hold_invoice.pay_speed', 'Pay from Phoenix or any Lightning wallet')} 
                        </div>
                        
                        <div className="pt-2">
                          <button
                            onClick={() => copyToClipboard(currentSwap.holdInvoice)}
                            className="w-full px-4 py-2 bg-apple-blue-500 text-white rounded-lg hover:bg-apple-blue-600 transition-colors"
                          >
                            {t('ta_swap.hold_invoice.copy')}
                          </button>
                        </div>
                        
                        <div className="text-xs text-apple-gray-500 break-all mt-2 p-2 bg-apple-gray-50 rounded">
                          {currentSwap.holdInvoice.substring(0, 60)}...
                        </div>
                      </div>
                    </div>
                  )}
                  {currentSwap.status === 'pending' && !currentSwap.isBuyer && (
                    <div className="bg-apple-yellow-50 border-2 border-apple-yellow-300 rounded-lg p-6 text-center">
                      <div className="text-4xl mb-3">⏳</div>
                      <h4 className="text-lg font-semibold text-apple-yellow-800 mb-2">
                        {t('ta_swap.hold_invoice.waiting_buyer_title', 'Waiting for buyer to pay')}
                      </h4>
                      <p className="text-sm text-apple-yellow-700">
                        {t('ta_swap.hold_invoice.waiting_buyer')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Step 2: DEX pays seller */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      currentSwap.status === 'asset_paid' || currentSwap.status === 'completed' 
                        ? 'bg-apple-green-500 text-white' 
                        : currentSwap.status === 'fee_paid'
                          ? 'bg-apple-yellow-500 text-white animate-pulse'
                          : 'bg-apple-gray-300 text-apple-gray-600'
                    }`}>
                      {currentSwap.status === 'asset_paid' || currentSwap.status === 'completed' ? '✓' : '2'}
                    </div>
                    <h3 className="text-lg font-semibold">
                      {currentSwap.status === 'asset_paid' || currentSwap.status === 'completed'
                        ? t('ta_swap.steps.seller_paid', 'Seller paid ✅') 
                        : currentSwap.status === 'fee_paid'
                          ? t('ta_swap.steps.paying_seller', 'Paying seller via Lightning...')
                          : t('ta_swap.steps.dex_pays_seller', 'DEX pays seller')}
                    </h3>
                  </div>

                  <div className={`rounded-lg p-4 ${
                    currentSwap.status === 'fee_paid'
                      ? 'bg-apple-yellow-50 border border-apple-yellow-200' 
                      : 'bg-apple-gray-50 border border-apple-gray-200'
                  }`}>
                    <p className="text-sm text-apple-gray-700">
                      {currentSwap.status === 'fee_paid'
                        ? t('ta_swap.steps.sending_to_seller_detail', 'The DEX is sending sats to the seller\'s Lightning Address...')
                        : currentSwap.status === 'asset_paid' || currentSwap.status === 'completed'
                          ? t('ta_swap.steps.seller_received', 'Seller has received their sats!')
                          : t('ta_swap.steps.sending_sats', 'Sending sats to the seller via Lightning...')}
                    </p>
                  </div>
                </div>

                {/* Step 3: Settlement */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      currentSwap.status === 'completed' 
                        ? 'bg-apple-green-500 text-white' 
                        : 'bg-apple-gray-300 text-apple-gray-600'
                    }`}>
                      {currentSwap.status === 'completed' ? '✓' : '3'}
                    </div>
                    <h3 className="text-lg font-semibold">
                      {currentSwap.status === 'completed' ? t('ta_swap.steps.settled', 'Settled ✅') : t('ta_swap.steps.settlement', 'Settlement')}
                    </h3>
                  </div>
                </div>

                {/* Expiry Timer */}
                {currentSwap.expiresAt && (
                  <div className="bg-apple-yellow-50 border border-apple-yellow-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Info className="w-4 h-4 text-apple-yellow-700" />
                      <span className="text-apple-yellow-800">
                        {t('ta_swap.banner.expires', 'Expires')}: {new Date(currentSwap.expiresAt).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                )}

                {/* Cancel Button */}
                {currentSwap.status === 'pending' && (
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={handleCancelSwap}
                  >
                    {t('ta_swap.cancel_swap', 'Cancel Swap')}
                  </Button>
                )}
              </>
            )}

            {/* Completed State */}
            {currentSwap.status === 'completed' && (
              <div className="text-center py-6">
                <div className="text-6xl mb-4">🎉</div>
                <p className="text-lg font-semibold mb-2">{t('ta_swap.completion.title')}</p>
                <p className="text-apple-gray-600 mb-6">
                  {t('ta_swap.completion.message')}
                </p>
                <Button variant="primary" onClick={handleCloseSwapModal}>
                  Close
                </Button>
              </div>
            )}

            {/* Failed/Expired/Cancelled close button */}
            {['expired', 'cancelled', 'failed'].includes(currentSwap.status) && (
              <div className="text-center py-4">
                <Button variant="primary" onClick={handleCloseSwapModal}>
                  Close
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
